import React, { useState } from 'react';
import { Tooltip, Empty, Button as AntdButton, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { SendOutlined, FolderOpenOutlined, NotificationTwoTone, HourglassOutlined } from '@ant-design/icons';
import { Constants } from 'core';
import type { Services, Segments, Circulations, SituationCategory } from 'types';
import { getTheme } from 'common/styling/theme';
import { isEspagne } from 'common/config/env';
import StyledTable from 'components/organisms/styled-table';
import { stationsServedSelectors, SendCommunicationActions, UserInfoSelectors } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import { openBlankWindow } from 'common/utils/window';
import PaginationBlock from 'components/molecules/pagination';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import { path } from 'ramda';

interface Props {
  data?: Circulations;
  onChangePage: (page: number, pageSize?: number) => void;
}

const WrapperTable = styled.div`
  .table-striped-rows tr:nth-child(2n) td {
    background-color: #ececec;
  }
`;
const StyledAntdButton = styled(AntdButton)`
  :hover {
    background-color: transparent !important;
  }
`;

const Label = styled.span`
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const Loader = styled.div`
  @-webkit-keyframes spin {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  #cover-spin {
    content: '';
    display: block;
    left: 48%;
    top: 40%;
    width: 40px;
    height: 40px;
    border-style: solid;
    border-color: black;
    border-top-color: transparent;
    border-width: 4px;
    border-radius: 50%;
    -webkit-animation: spin 0.8s linear infinite;
    animation: spin 0.8s linear infinite;
    position: fixed;
  }
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 9999;
  display: inline-block;
`;

const acceptStyle = (
  segments: Segments[],
  isCanceled?: boolean,
  isDepartureStopSkip?: boolean,
  isArrivalStopSkip?: boolean
) =>
  segments.length !== 1
    ? {
        borderBottom: '1px solid #f5f5f5',
        paddingTop: '5px',
        paddingBottom: '5px',
        color: isCanceled || isDepartureStopSkip || isArrivalStopSkip ? 'red' : undefined,
      }
    : { color: isCanceled || isDepartureStopSkip || isArrivalStopSkip ? 'red' : undefined };

const TimeWarningMessage: React.FC<{ selectedCirculation: Services }> = ({ selectedCirculation }) => {
  const { t } = useIntl();

  return (
    <div>
      <span>{t('Modal', 'TRAIN_WARNING', { serviceName: selectedCirculation?.serviceName })}</span>
      <Label>
        {t('Modal', 'TRAIN_WARNING_DATE', {
          serviceDay: moment(selectedCirculation?.circulationDate)
            .locale(isEspagne ? 'ES' : 'FR')
            .format('dddd'),
          serviceDate: moment(selectedCirculation?.circulationDate).format('DD/MM'),
        })}
      </Label>
    </div>
  );
};

const isInWarningPeriod = () => {
  const current = moment().format('HH:mm');
  const start = Constants.AFTER_TIME_WARNING;
  const end = Constants.BEFORE_TIME_WARNING;

  if (start <= end) {
    // Normal period within same day (e.g. 11:30 - 14:30)
    return current >= start && current <= end;
  } else {
    // Period spanning across midnight (e.g. 23:45 - 05:00 next day)
    // This means: from 23:45 until midnight OR from midnight until 05:00
    return current >= start || current <= end;
  }
};

const CirculationList: React.FC<Props> = ({ data, onChangePage }) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const stationsServedStatus = useSelector(stationsServedSelectors.statusSelector);
  const canWriteCommunications = useSelector(UserInfoSelectors.hasWriteAccess('Communication'));

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCirculation, setSelectedCirculation] = useState<Services>();
  const [situationCategory, setSituationCategory] = useState<SituationCategory>();

  const showModal = (record: Services, situationCategory: SituationCategory) => {
    setSelectedCirculation(record);
    setSituationCategory(situationCategory);
    setModalVisible(true);
  };

  const handleOk = () => {
    if (selectedCirculation && situationCategory) {
      initializeCommunication(selectedCirculation, situationCategory);
    }
    setModalVisible(false);
  };

  const initializeCommunication = (record: Services, situationCategory: SituationCategory) => {
    dispatch(
      SendCommunicationActions.fetchInitializeCommunication(
        {
          serviceName: record?.serviceName,
          startDate: record?.circulationDate ? moment(record.circulationDate).format('YYYY-MM-DD') : undefined,
          endDate: record?.circulationDate ? moment(record.circulationDate).format('YYYY-MM-DD') : undefined,
          IsBulkCommunication: false,
        },
        (result) => {
          if (!result.cancelled && !result.error) {
            const { data } = result;
            if (data.isValid) {
              dispatch(SendCommunicationActions.setIsBulkCommunication(false));
              openBlankWindow(`/send-communication/${situationCategory}`);
            }
          }
        }
      )
    );
  };
  const columns = [
    {
      title: t('list_circulation', 'CHART_TRANSPORTATION_PLAN'),
      dataIndex: 'Plan de transport',
      key: 'Plan de transport',
      children: [
        {
          title: () => ' ', // If you put '' directly, antd Table uses td instead of th and we loose the backgroundColor of the col title
          dataIndex: 'departureShortCodeStation',
          key: 'Départ',
          children: [
            {
              title: t('list_circulation', 'CHART_DATE'),
              render: (record: Services) => (
                <div style={{ paddingTop: 20, paddingBottom: 20, color: record.isCanceled ? 'red' : undefined }}>
                  {moment(record.circulationDate).format('DD/MM')}
                </div>
              ),
              key: 'Date',
            },
            {
              title: t('list_circulation', 'CHART_TRAIN'),
              render: (record: Services) => (
                <div style={{ paddingTop: 20, paddingBottom: 20, color: record.isCanceled ? 'red' : undefined }}>
                  {record.serviceName}
                </div>
              ),
              key: 'Train',
            },
          ],
        },
        {
          title: t('list_circulation', 'CHART_DEPARTURE'),
          dataIndex: 'departureShortCodeStation',
          key: 'Départ',
          children: [
            {
              title: t('list_circulation', 'CHART_STATION'),
              render: (record: Services) => (
                <div>
                  {record.segments.map((segment, index: number) => (
                    <div
                      style={acceptStyle(record.segments, record.isCanceled, segment.isDepartureStopSkip)}
                      key={index}
                    >
                      <Tooltip title={segment.departureStationFullName} placement="right">
                        {segment.departureShortCodeStation}
                      </Tooltip>
                    </div>
                  ))}
                </div>
              ),
              key: 'GareDepart',
            },
            {
              title: t('list_circulation', 'CHART_TIME'),
              render: (record: Services) => (
                <div>
                  {record.segments.map((segment, index: number) => (
                    <div
                      key={index}
                      style={acceptStyle(record.segments, record.isCanceled, segment.isDepartureStopSkip)}
                    >
                      {moment(segment.departureTime).format('HH:mm')}
                    </div>
                  ))}
                </div>
              ),
              key: 'HeureDepart',
            },
          ],
        },
        {
          title: t('list_circulation', 'CHART_ARRIVAL'),
          dataIndex: 'Arrivée',
          key: 'Arrivée',
          children: [
            {
              title: t('list_circulation', 'CHART_STATION'),
              render: (record: Services) => (
                <div>
                  {record.segments.map((segment, index: number) => (
                    <div style={acceptStyle(record.segments, record.isCanceled, segment.isArrivalStopSkip)} key={index}>
                      <Tooltip title={segment.arrivalStationFullName} placement="left">
                        {segment.arrivalShortCodeStation}
                      </Tooltip>
                    </div>
                  ))}
                </div>
              ),
              key: 'GareArrive',
            },
            {
              title: t('list_circulation', 'CHART_TIME'),
              render: (record: Services) => (
                <div>
                  {record.segments.map((segment, index: number) => (
                    <div key={index} style={acceptStyle(record.segments, record.isCanceled, segment.isArrivalStopSkip)}>
                      {moment(segment.arrivalTime).format('HH:mm')}
                    </div>
                  ))}
                </div>
              ),
              key: 'HeureArrive',
            },
          ],
        },
      ],
    },
    {
      title: t('list_circulation', 'CHART_COM_NB_PASSENGERS'),
      children: [
        {
          title: t('list_circulation', 'CHART_PASSENGERS_1'),
          render: (record: Services) => (
            <div>
              {record.segments.map((rec, index: number) => (
                <div key={index} style={acceptStyle(record.segments)}>
                  {rec.firstTrainNbPassengers}
                </div>
              ))}
            </div>
          ),
          key: 'nb8',
        },
        {
          title: t('list_circulation', 'CHART_PASSENGERS_2'),
          render: (record: Services) => (
            <div>
              {record.segments.map((rec, index: number) => (
                <div key={index} style={acceptStyle(record.segments)}>
                  {rec.secondTrainNbPassenger}
                </div>
              ))}
            </div>
          ),
          key: 'nb18',
        },
      ],
    },
    {
      title: t('list_circulation', 'CHART_COM_LAST_COM'),
      render: (record: Services) => (
        <Row>
          {record.hasCommunication && (
            <div>
              <FolderOpenOutlined
                onClick={() => {
                  openBlankWindow(`/historisation/${record.serviceName}/${record.circulationDate}`);
                }}
                style={{
                  fontSize: '28px',
                  color: `${getTheme().colors.grey.dark}`,
                }}
              />
            </div>
          )}
          {record.hasDifferedCommunication && (
            <div>
              <HourglassOutlined
                onClick={() => {
                  const params = { serviceName: record.serviceName, circulationDate: record.circulationDate };
                  openBlankWindow('/historisation-pending', params);
                }}
                style={{
                  fontSize: '28px',
                  color: `${getTheme().colors.grey.dark}`,
                }}
              />
            </div>
          )}
        </Row>
      ),
      key: 'Historique',
    },
    {
      title: t('list_circulation', 'CHART_COM_LAUNCH_COM'),
      children: [
        {
          title: t('list_circulation', 'CHART_PRE_OP_COM'),
          render: (record: Services) => (
            <Tooltip
              title={!canWriteCommunications && t('sendCommunication', 'differedCommunication', 'missingAuth')}
              placement="left"
            >
              {record.isTodaysCirculation ? (
                <StyledAntdButton
                  onClick={() => {
                    isInWarningPeriod()
                      ? showModal(record, Constants.SITUATION_CATEGORY.PRE_OP)
                      : initializeCommunication(record, Constants.SITUATION_CATEGORY.PRE_OP);
                  }}
                  disabled={!canWriteCommunications}
                  type="text"
                  icon={
                    <NotificationTwoTone
                      twoToneColor="#00a000"
                      style={{
                        fontSize: '28px',
                      }}
                    />
                  }
                />
              ) : (
                <StyledAntdButton
                  onClick={() => {
                    isInWarningPeriod()
                      ? showModal(record, Constants.SITUATION_CATEGORY.PRE_OP)
                      : initializeCommunication(record, Constants.SITUATION_CATEGORY.PRE_OP);
                  }}
                  disabled={!canWriteCommunications}
                  type="text"
                  icon={
                    <SendOutlined
                      rotate={315}
                      twoToneColor={`${getTheme().colors.grey.darker}`}
                      style={{
                        fontSize: '28px',
                        color: `${getTheme().colors.grey.darker}`,
                      }}
                    />
                  }
                />
              )}
            </Tooltip>
          ),
          key: 'CommunicationPreOp',
        },
        {
          title: t('list_circulation', 'CHART_OP_COM'),
          render: (record: Services) => (
            <Tooltip
              title={!canWriteCommunications && t('sendCommunication', 'differedCommunication', 'missingAuth')}
              placement="left"
            >
              {record.isTodaysCirculation ? (
                <StyledAntdButton
                  onClick={() => {
                    isInWarningPeriod()
                      ? showModal(record, Constants.SITUATION_CATEGORY.OP)
                      : initializeCommunication(record, Constants.SITUATION_CATEGORY.OP);
                  }}
                  disabled={!canWriteCommunications}
                  type="text"
                  icon={
                    <NotificationTwoTone
                      twoToneColor="#00a000"
                      style={{
                        fontSize: '28px',
                      }}
                    />
                  }
                />
              ) : (
                <StyledAntdButton
                  onClick={() => {
                    isInWarningPeriod()
                      ? showModal(record, Constants.SITUATION_CATEGORY.OP)
                      : initializeCommunication(record, Constants.SITUATION_CATEGORY.OP);
                  }}
                  disabled={!canWriteCommunications}
                  type="text"
                  icon={
                    <SendOutlined
                      rotate={315}
                      twoToneColor={`${getTheme().colors.grey.darker}`}
                      style={{
                        fontSize: '28px',
                        color: `${getTheme().colors.grey.darker}`,
                      }}
                    />
                  }
                />
              )}
            </Tooltip>
          ),
          key: 'CommunicationOp',
        },
      ],
    },
  ];

  const CirculationsDataSource = data?.result?.map((item, index) => ({
    key: `${item.serviceName}-${index}`,
    ...item,
  }));
  return (
    <>
      {stationsServedStatus === 'loading' && (
        <Loader>
          <div id="cover-spin" />
        </Loader>
      )}
      {selectedCirculation && (
        <Modal footer={null} open={modalVisible} onCancel={() => setModalVisible(false)}>
          <ModalContentConfirm
            row
            hideCancel
            onConfirm={handleOk}
            labelConfirm={t('Modal', 'CONTINUE')}
            title={<TimeWarningMessage selectedCirculation={selectedCirculation} />}
          />
        </Modal>
      )}
      <PaginationBlock
        data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
        onChange={(page: number, pageSize?: number) => onChangePage(page, pageSize)}
      />
      <WrapperTable>
        <StyledTable
          className="table-striped-rows"
          pagination={false}
          dataSource={CirculationsDataSource}
          padding="0"
          columns={columns}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
        />
      </WrapperTable>
      <PaginationBlock
        data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
        onChange={(page: number, pageSize?: number) => onChangePage(page, pageSize)}
      />
    </>
  );
};

export default CirculationList;
